import { useEffect, useRef, useState, useCallback, useLayoutEffect } from "react";

type SaveStatus = "saved" | "saving" | "unsaved" | "error";

export function useAutoSave<T>(
  data: T,
  saveFn: (data: T) => Promise<boolean>,
  delay: number = 2000
) {
  const [status, setStatus] = useState<SaveStatus>("saved");
  const previousDataRef = useRef<string>(JSON.stringify(data));
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isFirstRender = useRef(true);

  // Keep track of the latest data and save function for cleanup
  const latestDataRef = useRef(data);
  const saveFnRef = useRef(saveFn);

  useLayoutEffect(() => {
    latestDataRef.current = data;
    saveFnRef.current = saveFn;
  }, [data, saveFn]);

  const save = useCallback(async () => {
    setStatus("saving");
    try {
      const success = await saveFn(data);
      if (success) {
        setStatus("saved");
        previousDataRef.current = JSON.stringify(data);
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  }, [data, saveFn]);

  // Unmount cleanup: save if needed
  useEffect(() => {
    return () => {
      const currentDataStr = JSON.stringify(latestDataRef.current);
      if (currentDataStr !== previousDataRef.current) {
        // We can't await here, but we can trigger the save
        // Ideally saveFn should handle "beacon" updates or we accept best-effort
        console.log('[AutoSave] Unmount detected with unsaved changes, saving...');
        saveFnRef.current(latestDataRef.current).catch(err => 
          console.error('[AutoSave] Unmount save failed:', err)
        );
      }
    };
  }, []);

  useEffect(() => {
    // Skip first render
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    const currentData = JSON.stringify(data);
    
    // Check if data has changed
    if (currentData === previousDataRef.current) {
      return;
    }

    setStatus("unsaved");

    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set new timeout for auto-save
    timeoutRef.current = setTimeout(save, delay);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [data, delay, save]);

  const forceSave = useCallback(async () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    await save();
  }, [save]);

  const hasUnsavedChanges = status === "unsaved" || status === "saving";

  return {
    status,
    forceSave,
    hasUnsavedChanges,
  };
}
